// src/pages/brand-collaborations-client-stories/components/IndustryVerticals.jsx
import React from 'react';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';

const IndustryVerticals = ({ selectedIndustry, setSelectedIndustry }) => {
  const industries = [
    {
      id: 'technology',
      name: 'Technology',
      icon: 'Cpu',
      description: 'Product launch films and innovation showcases',
      projects: 45,
      specialties: ['Product Launches', 'Innovation Stories', 'Corporate Culture', 'Technical Documentation'],
      image: 'https://images.unsplash.com/photo-1518709268805-4e9042af2176?q=80&w=2125&auto=format&fit=crop',
      caseStudies: [
        {
          client: 'Global Tech Innovation',
          project: 'AI Product Launch Campaign',
          result: '500% engagement increase',
          image: 'https://images.pexels.com/photos/3861969/pexels-photo-3861969.jpeg?auto=compress&cs=tinysrgb&w=800'
        },
        {
          client: 'StartUp Accelerator',
          project: 'Innovation Showcase Series',
          result: '300% investor interest',
          image: 'https://images.pixabay.com/photo/2017/08/10/02/05/people-2619817_960_720.jpg'
        }
      ]
    },
    {
      id: 'real-estate',
      name: 'Real Estate',
      icon: 'Building',
      description: 'Luxury property presentations and development showcases',
      projects: 75,
      specialties: ['Luxury Properties', 'Virtual Tours', 'Architectural Photography', 'Development Marketing'],
      image: 'https://images.unsplash.com/photo-1560518883-ce09059eeffa?q=80&w=2073&auto=format&fit=crop',
      caseStudies: [
        {
          client: 'Platinum Properties',
          project: 'Luxury Penthouse Series',
          result: '250% sales acceleration',
          image: 'https://images.pexels.com/photos/1643383/pexels-photo-1643383.jpeg?auto=compress&cs=tinysrgb&w=800'
        },
        {
          client: 'Urban Development Co.',
          project: 'Residential Complex Launch',
          result: '400% pre-sales boost',
          image: 'https://images.pixabay.com/photo/2016/11/29/09/37/architecture-1868667_960_720.jpg'
        }
      ]
    },
    {
      id: 'events',
      name: 'Corporate Events',
      icon: 'Calendar',
      description: 'Seamless documentation of corporate milestones',
      projects: 120,
      specialties: ['Conference Coverage', 'Award Ceremonies', 'Product Launches', 'Executive Meetings'],
      image: 'https://images.unsplash.com/photo-1540575467063-178a50c2df87?q=80&w=2070&auto=format&fit=crop',
      caseStudies: [
        {
          client: 'Global Business Summit',
          project: 'Annual Conference Series',
          result: '300% attendance growth',
          image: 'https://images.pexels.com/photos/1709003/pexels-photo-1709003.jpeg?auto=compress&cs=tinysrgb&w=800'
        },
        {
          client: 'Innovation Awards',
          project: 'Awards Ceremony Coverage',
          result: '500% social media reach',
          image: 'https://images.unsplash.com/photo-1475721027785-f74eccf877e2?q=80&w=800&auto=format&fit=crop'
        }
      ]
    },
    {
      id: 'manufacturing',
      name: 'Manufacturing',
      icon: 'Settings',
      description: 'Industrial storytelling and process documentation',
      projects: 35,
      specialties: ['Process Documentation', 'Safety Training', 'Innovation Stories', 'Facility Tours'],
      image: 'https://images.unsplash.com/photo-1581091226825-a6a2a5aee158?q=80&w=2070&auto=format&fit=crop',
      caseStudies: [
        {
          client: 'Industrial Solutions Inc.',
          project: 'Manufacturing Excellence Series',
          result: '400% process efficiency',
          image: 'https://images.pexels.com/photos/3802510/pexels-photo-3802510.jpeg?auto=compress&cs=tinysrgb&w=800'
        },
        {
          client: 'Advanced Manufacturing',
          project: 'Innovation Showcase',
          result: '600% client inquiries',
          image: 'https://images.pixabay.com/photo/2017/08/10/02/05/people-2619817_960_720.jpg'
        }
      ]
    }
  ];

  const activeIndustry = industries.find(industry => industry.id === selectedIndustry);

  return (
    <section className="py-20 bg-surface">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-16">
          <h2 className="text-4xl md:text-5xl font-bold text-text-primary mb-6">
            Industry <span className="text-gradient-gold">Expertise</span>
          </h2>
          <p className="text-xl text-text-secondary max-w-3xl mx-auto leading-relaxed">
            Specialized knowledge across diverse sectors, delivering tailored solutions 
            that understand unique industry challenges and opportunities
          </p>
        </div>

        {/* Industry Navigation */}
        <div className="flex items-center justify-center mb-12">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {industries.map((industry) => (
              <button
                key={industry.id}
                onClick={() => setSelectedIndustry(industry.id)}
                className={`p-6 rounded-lg text-center transition-all duration-300 ${
                  selectedIndustry === industry.id
                    ? 'bg-accent text-white shadow-glow'
                    : 'bg-white text-text-primary hover:bg-accent/10 hover:shadow-subtle'
                }`}
              >
                <div className={`w-12 h-12 rounded-full flex items-center justify-center mx-auto mb-3 ${
                  selectedIndustry === industry.id ? 'bg-white/20' : 'bg-accent/10'
                }`}>
                  <Icon 
                    name={industry.icon} 
                    size={24} 
                    color={selectedIndustry === industry.id ? '#FFFFFF' : '#D4AF37'} 
                    strokeWidth={2} 
                  />
                </div>
                <h3 className="font-bold text-sm mb-1">{industry.name}</h3>
                <p className="text-xs opacity-80">{industry.projects} Projects</p>
              </button>
            ))}
          </div>
        </div>

        {/* Active Industry Content */}
        {activeIndustry && (
          <div className="animate-fade-in">
            {/* Industry Overview */}
            <div className="grid lg:grid-cols-2 gap-12 items-center mb-16">
              <div>
                <h3 className="text-3xl font-bold text-text-primary mb-4">
                  {activeIndustry.name} Solutions
                </h3>
                <p className="text-lg text-text-secondary mb-6 leading-relaxed">
                  {activeIndustry.description}
                </p>
                
                {/* Specialties */}
                <div className="mb-6">
                  <h4 className="font-bold text-text-primary mb-3">Core Specialties</h4>
                  <div className="grid grid-cols-2 gap-2">
                    {activeIndustry.specialties.map((specialty, index) => (
                      <div key={index} className="flex items-center space-x-2">
                        <Icon name="CheckCircle" size={16} className="text-accent" strokeWidth={2} />
                        <span className="text-text-secondary text-sm">{specialty}</span>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Industry Stats */}
                <div className="bg-white rounded-lg p-6 shadow-subtle">
                  <div className="grid grid-cols-3 gap-4 text-center">
                    <div>
                      <div className="text-2xl font-bold text-accent mb-1">{activeIndustry.projects}</div>
                      <div className="text-text-secondary text-xs">Completed Projects</div>
                    </div>
                    <div>
                      <div className="text-2xl font-bold text-accent mb-1">98%</div>
                      <div className="text-text-secondary text-xs">Client Satisfaction</div>
                    </div>
                    <div>
                      <div className="text-2xl font-bold text-accent mb-1">85%</div>
                      <div className="text-text-secondary text-xs">Repeat Business</div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="relative">
                <Image
                  src={activeIndustry.image}
                  alt={`${activeIndustry.name} industry showcase`}
                  className="w-full h-96 object-cover rounded-lg shadow-elevation"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/40 to-transparent rounded-lg"></div>
              </div>
            </div>

            {/* Case Studies */}
            <div>
              <h4 className="text-2xl font-bold text-text-primary mb-8 text-center">
                Featured {activeIndustry.name} Case Studies
              </h4>
              <div className="grid md:grid-cols-2 gap-8">
                {activeIndustry.caseStudies.map((study, index) => (
                  <div key={index} className="bg-white rounded-lg overflow-hidden shadow-subtle hover:shadow-elevation transition-all duration-300">
                    <div className="relative h-48">
                      <Image
                        src={study.image}
                        alt={study.project}
                        className="w-full h-full object-cover"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent"></div>
                      <div className="absolute bottom-4 left-4 right-4">
                        <h5 className="text-white font-bold text-lg mb-1">{study.client}</h5>
                        <p className="text-white/80 text-sm">{study.project}</p>
                      </div>
                    </div>
                    <div className="p-6">
                      <div className="flex items-center space-x-2 text-accent font-semibold">
                        <Icon name="TrendingUp" size={18} strokeWidth={2} />
                        <span>{study.result}</span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>
    </section>
  );
};

export default IndustryVerticals;